import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
  Avatar,
  Tooltip,
  Skeleton,
  alpha,
  useTheme,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  MenuItem,
  Select,
  FormControl,
  Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { showAuth } from '../../redux/authSlice';
import DiscussionMarkdown from './DiscussionMarkdown';
import DiscussionComposer from './discussion/DiscussionComposer';
import { DiscussionCommunityNotice } from './discussion/DiscussionPostingGuidelines';
import {
  ACCENT,
  ACCENT_HOVER,
  DISCUSSION_TAGS,
  TAG_COLORS,
  timeAgo,
  insertCommentInTree,
  removeCommentFromTree,
  updateCommentInTree,
} from './discussion/discussionUtils';
import {
  useProblemDiscussions,
  createDiscussionPost,
  postDiscussionComment,
  togglePostUpvote,
  toggleCommentUpvote,
  deleteDiscussionPost,
  deleteDiscussionComment,
} from '../../hooks/useProblemDiscussions';
import { resolveProfileImage } from '../../common/utils';

const AuthorRow = ({ author, when, isDark, muted, size = 'sm' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <Avatar
      src={resolveProfileImage(author?.profileImage)}
      sx={{
        width: size === 'lg' ? 32 : 24,
        height: size === 'lg' ? 32 : 24,
        fontSize: size === 'lg' ? '0.85rem' : '0.68rem',
        bgcolor: ACCENT,
        flexShrink: 0,
      }}
    >
      {(author?.name || author?.username || '?').charAt(0).toUpperCase()}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        noWrap
        sx={{ fontWeight: 700, color: isDark ? '#e6edf3' : '#1f2933', display: 'block' }}
      >
        {author?.name || author?.username || 'User'}
        {author?.role === 'admin' && (
          <Chip
            label="Admin"
            size="small"
            sx={{ ml: 0.5, height: 16, fontSize: '0.58rem', fontWeight: 800, verticalAlign: 'middle' }}
          />
        )}
      </Typography>
      {when && (
        <Typography variant="caption" sx={{ color: muted, fontSize: '0.68rem' }}>
          {when}
        </Typography>
      )}
    </Box>
  </Box>
);

const VoteButton = ({ count, upvoted, onClick, muted: mutedColor = '#8b949e' }) => (
  <Box
    component="span"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      cursor: 'pointer',
      userSelect: 'none',
      borderRadius: 1,
      px: 0.5,
      py: 0.25,
      color: upvoted ? ACCENT : mutedColor,
      '&:hover': { bgcolor: alpha(ACCENT, 0.08) },
    }}
  >
    {upvoted ? (
      <ThumbUpIcon sx={{ fontSize: 18, display: 'block' }} />
    ) : (
      <ThumbUpAltOutlinedIcon sx={{ fontSize: 18, display: 'block' }} />
    )}
    <Typography
      component="span"
      sx={{ fontWeight: 800, fontSize: '0.8rem', lineHeight: 1, minWidth: 12 }}
    >
      {count ?? 0}
    </Typography>
  </Box>
);

const StatBadge = ({ icon: Icon, value, muted: mutedColor }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    <Icon sx={{ fontSize: 15, color: mutedColor, display: 'block' }} />
    <Typography component="span" variant="caption" sx={{ color: mutedColor, fontWeight: 700, lineHeight: 1 }}>
      {value ?? 0}
    </Typography>
  </Box>
);

const ListSkeleton = ({ border }) => (
  <>
    {[1, 2, 3].map((i) => (
      <Box key={i} sx={{ p: 1.5, mb: 1, borderRadius: 2, border: `1px solid ${border}` }}>
        <Skeleton width="40%" height={18} sx={{ mb: 1 }} />
        <Skeleton width="85%" height={22} sx={{ mb: 0.75 }} />
        <Skeleton width="55%" height={16} />
      </Box>
    ))}
  </>
);

const ThreadSkeleton = ({ border }) => (
  <Box sx={{ p: 2 }}>
    <Skeleton width="70%" height={28} sx={{ mb: 1.5 }} />
    <Skeleton width="35%" height={18} sx={{ mb: 2 }} />
    <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 2 }} />
    {[1, 2].map((i) => (
      <Skeleton key={i} height={64} sx={{ mb: 1, borderRadius: 2 }} />
    ))}
  </Box>
);

const CommentItem = ({
  comment,
  postId,
  isLoggedIn,
  isDark,
  border,
  muted,
  onAuth,
  onOptimisticUpvote,
  onOptimisticReply,
  onOptimisticDelete,
  onDeleteRollback,
  onReplaceComment,
  onReplyRollback,
  depth = 0,
}) => {
  const [replyOpen, setReplyOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleUpvote = async () => {
    if (!isLoggedIn) return onAuth();
    const prev = { upvoted: comment.upvoted, upvoteCount: comment.upvoteCount };
    onOptimisticUpvote(comment.id, {
      upvoted: !comment.upvoted,
      upvoteCount: Math.max(0, (comment.upvoteCount || 0) + (comment.upvoted ? -1 : 1)),
    });
    try {
      const res = await toggleCommentUpvote(comment.id);
      onOptimisticUpvote(comment.id, { upvoted: res.upvoted, upvoteCount: res.upvoteCount });
    } catch {
      onOptimisticUpvote(comment.id, prev);
      toast.error('Could not update vote');
    }
  };

  const handleReply = async (body) => {
    const tempId = `temp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      body,
      depth: (comment.depth || 0) + 1,
      parentId: comment.id,
      upvoteCount: 0,
      replyCount: 0,
      upvoted: false,
      createdAt: new Date().toISOString(),
      author: { name: 'You', username: 'you' },
      isOwner: true,
      replies: [],
      pending: true,
    };
    onOptimisticReply(optimistic, comment.id);
    setReplyOpen(false);
    try {
      const saved = await postDiscussionComment(postId, body, comment.id);
      onReplaceComment(tempId, { ...saved, replies: [] }, comment.id);
    } catch (err) {
      onOptimisticDelete(tempId);
      onReplyRollback?.();
      toast.error(err?.response?.data?.message || 'Could not post reply');
    }
  };

  const handleDelete = async () => {
    const snapshot = { ...comment, replies: comment.replies ? [...comment.replies] : [] };
    const parentId = comment.parentId ?? null;
    onOptimisticDelete(comment.id);
    try {
      await deleteDiscussionComment(comment.id);
      toast.success('Comment removed');
    } catch (err) {
      onDeleteRollback(snapshot, parentId);
      toast.error(err?.response?.data?.message || 'Could not delete');
    }
    setConfirmDelete(false);
  };

  return (
    <Box
      sx={{
        ml: depth > 0 ? 2 : 0,
        pl: depth > 0 ? 1.25 : 0,
        borderLeft: depth > 0 ? `2px solid ${alpha(ACCENT, 0.18)}` : 'none',
        mb: 1.25,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1.25,
          p: 1.25,
          borderRadius: 2,
          border: `1px solid ${border}`,
          bgcolor: isDark ? '#161b22' : '#fff',
          opacity: comment.pending ? 0.72 : 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
            <AuthorRow author={comment.author} when={timeAgo(comment.createdAt)} isDark={isDark} muted={muted} />
          </Box>
          <DiscussionMarkdown content={comment.body} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <VoteButton
              count={comment.upvoteCount}
              upvoted={comment.upvoted}
              onClick={handleUpvote}
              muted={muted}
            />
            {(comment.depth ?? 0) < 2 && (
              <Button
                size="small"
                startIcon={<ReplyRoundedIcon sx={{ fontSize: 15 }} />}
                onClick={() => {
                  if (!isLoggedIn) return onAuth();
                  setReplyOpen((v) => !v);
                }}
                sx={{ textTransform: 'none', color: muted, fontWeight: 600, minWidth: 0 }}
              >
                Reply
              </Button>
            )}
            {comment.isOwner && !confirmDelete && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => setConfirmDelete(true)} sx={{ color: muted }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            {confirmDelete && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: muted }}>
                  Delete?
                </Typography>
                <Button size="small" color="error" onClick={handleDelete} sx={{ textTransform: 'none', minWidth: 0 }}>
                  Yes
                </Button>
                <Button size="small" onClick={() => setConfirmDelete(false)} sx={{ textTransform: 'none', minWidth: 0 }}>
                  No
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Collapse in={replyOpen}>
        <Box sx={{ mt: 1, ml: 5 }}>
          <DiscussionComposer
            compact
            autoFocus
            showCancel
            showGuidelines
            guidelineVariant="reply"
            mention={
              comment.author?.username
                ? { username: comment.author.username, name: comment.author.name }
                : null
            }
            placeholder={`Reply to ${comment.author?.name || 'comment'}…`}
            submitLabel="Reply"
            onSubmit={handleReply}
            onCancel={() => setReplyOpen(false)}
            isDark={isDark}
            border={border}
          />
        </Box>
      </Collapse>

      {(comment.replies || []).map((reply) => (
        <CommentItem
          key={reply.id}
          comment={reply}
          postId={postId}
          isLoggedIn={isLoggedIn}
          isDark={isDark}
          border={border}
          muted={muted}
          onAuth={onAuth}
            onOptimisticUpvote={onOptimisticUpvote}
            onOptimisticReply={onOptimisticReply}
            onOptimisticDelete={onOptimisticDelete}
            onDeleteRollback={onDeleteRollback}
            onReplaceComment={onReplaceComment}
            onReplyRollback={onReplyRollback}
            depth={depth + 1}
        />
      ))}
    </Box>
  );
};

const CreateTopicForm = ({ slug, isDark, border, onCreated, onCancel }) => {
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState(['General']);
  const [submitting, setSubmitting] = useState(false);

  const handlePublish = async (body) => {
    if (title.trim().length < 5) {
      toast.error('Title must be at least 5 characters');
      return;
    }
    setSubmitting(true);
    try {
      const post = await createDiscussionPost(slug, { title: title.trim(), body, tags });
      toast.success('Topic published');
      onCreated(post);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not publish topic');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        p: 1.5,
        mb: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha(ACCENT, 0.35)}`,
        bgcolor: isDark ? '#161b22' : '#fff',
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', mb: 1.25 }}>New topic</Typography>
      <TextField
        fullWidth
        size="small"
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="e.g. Two-pointer approach explained"
        sx={{ mb: 1.25 }}
      />
      <Autocomplete
        multiple
        size="small"
        options={DISCUSSION_TAGS}
        value={tags}
        onChange={(_, v) => setTags(v.length ? v : ['General'])}
        renderInput={(params) => <TextField {...params} label="Tags" />}
        sx={{ mb: 1.25 }}
      />
      <DiscussionComposer
        compact
        guidelineVariant="topic"
        placeholder="Share your question, solution, or code…"
        submitLabel={submitting ? 'Publishing…' : 'Publish'}
        onSubmit={handlePublish}
        isDark={isDark}
        border={border}
      />
      <Button size="small" onClick={onCancel} sx={{ mt: 1, textTransform: 'none' }}>
        Cancel
      </Button>
    </Box>
  );
};

const ProblemDiscussionTab = ({
  slug,
  isLoggedIn,
  active,
  enabled = true,
  initialPostId = null,
  onCountChange,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const panelBg = isDark ? '#0d1117' : '#f6f8fa';

  const {
    items,
    pagination,
    sort,
    setSort,
    tag,
    setTag,
    search,
    setSearch,
    listLoading,
    listError,
    selectedPostId,
    thread,
    threadLoading,
    threadError,
    selectPost,
    clearSelection,
    toggleExpand,
    fetchList,
    prependPost,
    removePostFromList,
    patchListItem,
    patchThreadPost,
    patchThreadComments,
  } = useProblemDiscussions({ slug, isLoggedIn, active, enabled });

  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    if (initialPostId && active) {
      selectPost(initialPostId);
    }
  }, [initialPostId, active, selectPost]);

  useEffect(() => {
    onCountChange?.(pagination.total);
  }, [pagination.total, onCountChange]);

  const requireAuth = () => dispatch(showAuth());

  const handlePostUpvote = async (post) => {
    if (!isLoggedIn) return requireAuth();
    const prev = { upvoted: post.upvoted, upvoteCount: post.upvoteCount };
    const next = {
      upvoted: !post.upvoted,
      upvoteCount: Math.max(0, (post.upvoteCount || 0) + (post.upvoted ? -1 : 1)),
    };
    patchListItem(post.id, next);
    patchThreadPost(next);
    try {
      const res = await togglePostUpvote(post.id);
      patchListItem(post.id, res);
      patchThreadPost(res);
    } catch {
      patchListItem(post.id, prev);
      patchThreadPost(prev);
      toast.error('Could not update vote');
    }
  };

  const handleTopComment = async (body) => {
    if (!thread?.post) return;
    const tempId = `temp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      body,
      depth: 0,
      parentId: null,
      upvoteCount: 0,
      replyCount: 0,
      upvoted: false,
      createdAt: new Date().toISOString(),
      author: { name: 'You', username: 'you' },
      isOwner: true,
      replies: [],
      pending: true,
    };
    patchThreadComments((c) => insertCommentInTree(c, optimistic));
    patchThreadPost({ commentCount: (thread.post.commentCount || 0) + 1 });
    patchListItem(thread.post.id, { commentCount: (thread.post.commentCount || 0) + 1 });
    try {
      const saved = await postDiscussionComment(thread.post.id, body);
      patchThreadComments((c) => {
        const without = removeCommentFromTree(c, tempId);
        return insertCommentInTree(without, { ...saved, replies: [] });
      });
    } catch (err) {
      patchThreadComments((c) => removeCommentFromTree(c, tempId));
      patchThreadPost({ commentCount: thread.post.commentCount });
      patchListItem(thread.post.id, { commentCount: thread.post.commentCount });
      toast.error(err?.response?.data?.message || 'Could not post comment');
    }
  };

  const handleDeletePost = async () => {
    if (!thread?.post) return;
    const id = thread.post.id;
    removePostFromList(id);
    clearSelection();
    try {
      await deleteDiscussionPost(id);
      toast.success('Topic deleted');
    } catch (err) {
      fetchList({ silent: false });
      toast.error(err?.response?.data?.message || 'Could not delete');
    }
  };

  const commentHandlers = useMemo(
    () => ({
      onOptimisticUpvote: (commentId, patch) => {
        patchThreadComments((c) => updateCommentInTree(c, commentId, patch));
      },
      onOptimisticReply: (comment, parentId) => {
        patchThreadComments((c) => insertCommentInTree(c, comment, parentId));
        if (thread?.post) {
          patchThreadPost({ commentCount: (thread.post.commentCount || 0) + 1 });
          patchListItem(thread.post.id, { commentCount: (thread.post.commentCount || 0) + 1 });
        }
      },
      onOptimisticDelete: (commentId) => {
        patchThreadComments((c) => removeCommentFromTree(c, commentId));
        if (thread?.post) {
          const next = Math.max(0, (thread.post.commentCount || 0) - 1);
          patchThreadPost({ commentCount: next });
          patchListItem(thread.post.id, { commentCount: next });
        }
      },
      onDeleteRollback: (comment, parentId) => {
        patchThreadComments((c) => insertCommentInTree(c, comment, parentId));
        if (thread?.post) {
          patchThreadPost({ commentCount: (thread.post.commentCount || 0) + 1 });
          patchListItem(thread.post.id, { commentCount: (thread.post.commentCount || 0) + 1 });
        }
      },
      onReplaceComment: (tempId, saved, parentId) => {
        patchThreadComments((c) => {
          const without = removeCommentFromTree(c, tempId);
          return insertCommentInTree(without, saved, parentId);
        });
      },
      onReplyRollback: () => {
        if (!thread?.post) return;
        const next = Math.max(0, (thread.post.commentCount || 0) - 1);
        patchThreadPost({ commentCount: next });
        patchListItem(thread.post.id, { commentCount: next });
      },
    }),
    [patchThreadComments, patchThreadPost, patchListItem, thread?.post],
  );

  const renderExpandedThread = (post) => {
    if (selectedPostId !== post.id) return null;

    if (threadLoading && !thread) {
      return <ThreadSkeleton border={border} />;
    }
    if (threadError && !thread) {
      return (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {threadError}
          </Typography>
          <Button size="small" onClick={() => selectPost(post.id)} sx={{ textTransform: 'none' }}>
            Retry
          </Button>
        </Box>
      );
    }
    if (!thread?.post) return null;

    return (
      <Box sx={{ pt: 1.25, pb: 0.5 }}>
        <Box sx={{ mb: 1.5 }}>
          <AuthorRow
            author={thread.post.author}
            when={`${timeAgo(thread.post.createdAt)} · ${thread.post.viewCount || 0} views`}
            isDark={isDark}
            muted={muted}
            size="lg"
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <VoteButton
            count={thread.post.upvoteCount}
            upvoted={thread.post.upvoted}
            onClick={() => handlePostUpvote(thread.post)}
            muted={muted}
          />
          <StatBadge icon={ChatBubbleOutlineIcon} value={thread.post.commentCount} muted={muted} />
          {thread.post.isOwner && (
            <Tooltip title="Delete topic">
              <IconButton size="small" onClick={handleDeletePost} sx={{ color: muted, ml: 'auto' }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <DiscussionMarkdown content={thread.post.body} />

        <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${border}` }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', mb: 1 }}>
            {thread.post.commentCount || 0} Comments
          </Typography>

          {isLoggedIn ? (
            <Box sx={{ mb: 1.5 }}>
              <DiscussionComposer
                placeholder="Add to the discussion…"
                submitLabel="Comment"
                onSubmit={handleTopComment}
                isDark={isDark}
                border={border}
              />
            </Box>
          ) : (
            <Button
              fullWidth
              variant="outlined"
              onClick={requireAuth}
              sx={{ mb: 1.5, textTransform: 'none', fontWeight: 700, borderStyle: 'dashed' }}
            >
              Sign in to comment
            </Button>
          )}

          {(thread.comments || []).length === 0 ? (
            <Typography variant="body2" sx={{ color: muted, textAlign: 'center', py: 1.5 }}>
              No comments yet — start the thread.
            </Typography>
          ) : (
            thread.comments.map((c) => (
              <CommentItem
                key={c.id}
                comment={c}
                postId={thread.post.id}
                isLoggedIn={isLoggedIn}
                isDark={isDark}
                border={border}
                muted={muted}
                onAuth={requireAuth}
                {...commentHandlers}
              />
            ))
          )}
        </Box>
      </Box>
    );
  };

  const listPane = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        bgcolor: panelBg,
      }}
    >
      <Box sx={{ p: 1.5, pb: 1, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ForumOutlinedIcon sx={{ color: ACCENT, fontSize: 20 }} />
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>Community</Typography>
            {pagination.total > 0 && (
              <Chip label={pagination.total} size="small" sx={{ height: 20, fontWeight: 700, fontSize: '0.65rem' }} />
            )}
          </Box>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => {
              if (!isLoggedIn) return requireAuth();
              setCreateOpen((v) => !v);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              bgcolor: ACCENT,
              '&:hover': { bgcolor: ACCENT_HOVER },
            }}
          >
            Topic
          </Button>
        </Box>

        <DiscussionCommunityNotice isDark={isDark} border={border} />

        <TextField
          fullWidth
          size="small"
          placeholder="Search topics…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: muted }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />

        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={sort}
            onChange={(_, v) => v && setSort(v)}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.72rem',
                px: 1.1,
                py: 0.35,
                borderColor: border,
              },
              '& .Mui-selected': { bgcolor: `${alpha(ACCENT, 0.12)} !important`, color: ACCENT },
            }}
          >
            <ToggleButton value="newest">
              <ScheduleIcon sx={{ fontSize: 14, mr: 0.4 }} /> New
            </ToggleButton>
            <ToggleButton value="popular">
              <TrendingUpIcon sx={{ fontSize: 14, mr: 0.4 }} /> Top
            </ToggleButton>
            {isLoggedIn && (
              <ToggleButton value="mine">
                <PersonOutlineIcon sx={{ fontSize: 14, mr: 0.4 }} /> Mine
              </ToggleButton>
            )}
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              displayEmpty
              sx={{ fontSize: '0.75rem', fontWeight: 600 }}
            >
              <MenuItem value="all">All tags</MenuItem>
              {DISCUSSION_TAGS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Collapse in={createOpen}>
        <Box sx={{ px: 1.5 }}>
          <CreateTopicForm
            slug={slug}
            isDark={isDark}
            border={border}
            onCreated={(post) => {
              prependPost(post);
              setCreateOpen(false);
              selectPost(post.id);
            }}
            onCancel={() => setCreateOpen(false)}
          />
        </Box>
      </Collapse>

      <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, pb: 1.5 }}>
        {listLoading && <ListSkeleton border={border} />}

        {!listLoading && listError && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {listError}
            </Typography>
            <Button size="small" onClick={() => fetchList()} sx={{ textTransform: 'none' }}>
              Retry
            </Button>
          </Box>
        )}

        {!listLoading && !listError && items.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, px: 1 }}>
            <ForumOutlinedIcon sx={{ fontSize: 36, color: muted, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No topics yet</Typography>
            <Typography variant="body2" sx={{ color: muted }}>
              Start a conversation — ask questions or share solutions.
            </Typography>
          </Box>
        )}

        {!listLoading &&
          items.map((post) => {
            const expanded = selectedPostId === post.id;
            return (
              <Box
                key={post.id}
                sx={{
                  mb: 0.85,
                  borderRadius: 2,
                  border: `1px solid ${expanded ? alpha(ACCENT, 0.45) : border}`,
                  bgcolor: surface,
                  overflow: 'hidden',
                  transition: 'border-color 0.15s ease',
                }}
              >
                <Box
                  onClick={() => toggleExpand(post.id)}
                  sx={{
                    p: 1.25,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(ACCENT, isDark ? 0.06 : 0.03) },
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.6, alignItems: 'center' }}>
                    {(post.tags || []).slice(0, 2).map((t) => (
                      <Chip
                        key={t}
                        label={t}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          bgcolor: alpha(TAG_COLORS[t] || ACCENT, isDark ? 0.22 : 0.12),
                          color: TAG_COLORS[t] || ACCENT,
                        }}
                      />
                    ))}
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', color: muted }}>
                      {expanded ? (
                        <ExpandLessIcon sx={{ fontSize: 20 }} />
                      ) : (
                        <ExpandMoreIcon sx={{ fontSize: 20 }} />
                      )}
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.88rem',
                      lineHeight: 1.35,
                      mb: 0.65,
                    }}
                  >
                    {post.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <AuthorRow author={post.author} isDark={isDark} muted={muted} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
                      <StatBadge icon={ThumbUpAltOutlinedIcon} value={post.upvoteCount} muted={muted} />
                      <StatBadge icon={ChatBubbleOutlineIcon} value={post.commentCount} muted={muted} />
                    </Box>
                  </Box>
                </Box>

                <Collapse in={expanded}>
                  <Box
                    sx={{
                      px: 1.25,
                      pb: 1.25,
                      borderTop: `1px solid ${border}`,
                      bgcolor: isDark ? '#0d1117' : '#fafbfc',
                    }}
                  >
                    {renderExpandedThread(post)}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 420,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderTop: `1px solid ${border}`,
      }}
    >
      {listPane}
    </Box>
  );
};

export default ProblemDiscussionTab;
